'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { useAnalytics } from './useAnalytics'
import { MTAG_EVENTS, SOURCE_APP } from '../config/eventNames'
import { omitEmpty } from '../utils/omitEmpty'
import type { ProductListViewed } from '@material-bank/analytics-events-ts/mtag'

export type ProductListViewedProduct = NonNullable<
  ProductListViewed['products']
>[number]

type ProductListViewedInput = {
  category: ProductListViewed['category']
  itemLocationContext: ProductListViewed['item_location_context']
  viewMode: ProductListViewed['view_mode']
  products: ProductListViewedProduct[]
  url?: string
}

const POSITION_ATTRIBUTE = 'data-list-position'

// How much of a tile has to be on screen for it to count as seen.
const SEEN_THRESHOLD = 0.5

// Tiles that come into view within this window are reported together, so the first
// paint or a fling sends one event rather than one per tile.
const BATCH_DELAY_MS = 300

// Spread onto each tile's element so the list observer can tell which product it is.
// `position` is the product's `position` in the list passed to the hook.
export function productListItemAttributes(position: number) {
  return { [POSITION_ATTRIBUTE]: position }
}

// Fires the canonical `product_list_viewed` for the products the user has actually seen:
// one event when the first tiles come into view, then one more for each batch of tiles
// scrolled into view after that. A product is reported once per list; a new set of
// products (another filter or sort) starts over. Attach the returned callback ref to the
// list container, and spread `productListItemAttributes(position)` onto each tile.
// `enabled` gates the observer — pass false for off-screen warm-up copies (e.g. the
// interstitial grid) so tiles under a hidden layout box can't count as seen.
export function useProductListViewed<T extends HTMLElement = HTMLDivElement>(
  input: ProductListViewedInput | null,
  enabled = true,
) {
  const { track, isReady } = useAnalytics()
  const [element, setElement] = useState<T | null>(null)
  const refCallback = useCallback((node: T | null) => setElement(node), [])
  const inputRef = useRef(input)
  inputRef.current = input
  const seenRef = useRef(new Set<number>())
  const seenListKeyRef = useRef('')
  // Reactive so the observer re-runs when a list that mounted empty fills, or when a new
  // result set replaces the old one.
  const listKey = input?.products.map((product) => product.sku).join('|') ?? ''

  const fire = useCallback(
    (data: ProductListViewedInput, positions: Set<number>) => {
      const products = data.products.filter((product) =>
        positions.has(product.position),
      )
      if (products.length === 0) {
        return
      }

      const payload = omitEmpty({
        category: data.category,
        item_location_context: data.itemLocationContext,
        // Required by the schema. Every mtag list is a single unpaginated grid.
        page_number: 1,
        view_mode: data.viewMode,
        // The size of the list, not of this batch; `products` carries the batch.
        results_count: data.products.length,
        products,
        source_app: SOURCE_APP,
        url:
          data.url ??
          (typeof window !== 'undefined' ? window.location.href : ''),
      }) as ProductListViewed

      track(MTAG_EVENTS.PRODUCT_LIST_VIEWED, payload)
    },
    [track],
  )

  useEffect(() => {
    const data = inputRef.current
    if (
      !element ||
      !enabled ||
      !isReady ||
      !data ||
      listKey === '' ||
      typeof IntersectionObserver === 'undefined'
    ) {
      return
    }

    if (seenListKeyRef.current !== listKey) {
      seenListKeyRef.current = listKey
      seenRef.current = new Set()
    }

    // Narrowed for the closures below.
    const list = data
    const seen = seenRef.current
    const pending = new Set<number>()
    let timer: ReturnType<typeof setTimeout> | null = null

    function flush() {
      timer = null
      if (pending.size === 0) {
        return
      }

      const batch = new Set(pending)
      pending.clear()
      batch.forEach((position) => seen.add(position))
      fire(list, batch)
    }

    // The viewport root clips against every scrolling ancestor, so a tile scrolled out
    // of a nested scroller is not reported as seen.
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const position = Number(entry.target.getAttribute(POSITION_ATTRIBUTE))
          if (
            !entry.isIntersecting ||
            !Number.isInteger(position) ||
            seen.has(position)
          ) {
            continue
          }

          pending.add(position)
          observer.unobserve(entry.target)
        }

        if (pending.size > 0 && !timer) {
          timer = setTimeout(flush, BATCH_DELAY_MS)
        }
      },
      { threshold: SEEN_THRESHOLD },
    )

    element
      .querySelectorAll(`[${POSITION_ATTRIBUTE}]`)
      .forEach((tile) => observer.observe(tile))

    // A batch still waiting is dropped, not flushed: on browser back the URL has already
    // changed by the time this runs, so it would be reported against the next page, and a
    // tile on screen for under the batch window before the user left was barely seen.
    return () => {
      observer.disconnect()
      if (timer) {
        clearTimeout(timer)
      }
    }
  }, [element, fire, isReady, enabled, listKey])

  return refCallback
}
